/**
 * Client-side session/action state for Medical Necessity analytics.
 * @module medicalNecessityAnalytics/sessionState
 */
import { IncorrectReasonType, RhsStage } from "./mappers";

/**
 * Generates a unique analytics action or session ID.
 *
 * @returns UUID string
 */
export const createAnalyticsId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `mn-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/** Pending Incorrect feedback from Save Feedback click, awaiting navigate persist. */
export interface PendingFeedbackAction {
  actionId: string;
  criterionId: string;
  feedbackType: IncorrectReasonType;
  /** Saved comment text, present only for the `comment` feedback type. */
  comment?: string;
  /**
   * Action id of the originating `reference_comment_selected` click, present
   * only for the `comment` feedback type; correlates the ensuing
   * `comment_saved` back to that click.
   */
  commentActionId?: string;
}

/** View surfaces that dedupe analytics emits by identity. */
type ViewSurface = "guideline" | "criterion" | "verdict" | "summary";

/**
 * Mutable client analytics session for one Medical Necessity route visit.
 */
export class MedicalNecessityAnalyticsSession {
  readonly reviewSessionId: string = createAnalyticsId();

  private criterionVisits = new Map<string, number>();

  private lastViewIdentity: Record<ViewSurface, string | null> = {
    guideline: null,
    criterion: null,
    verdict: null,
    summary: null,
  };

  private pendingFeedback: PendingFeedbackAction | null = null;

  private rhsStage: RhsStage = "criterion";

  private locationSnapshot: Record<string, unknown> = {};

  /** @returns Current rhs_stage */
  getRhsStage(): RhsStage {
    return this.rhsStage;
  }

  /**
   * Updates the active RHS stage.
   *
   * @param stage - New rhs_stage
   */
  setRhsStage(stage: RhsStage): void {
    this.rhsStage = stage;
  }

  /**
   * Stores the current guideline/criterion position for PDF events.
   *
   * @param snapshot - Location attributes (empty on summary)
   */
  setLocationSnapshot(snapshot: Record<string, unknown>): void {
    this.locationSnapshot = snapshot;
  }

  /** @returns Location attribute bag for PDF events */
  getLocationSnapshot(): Record<string, unknown> {
    return this.locationSnapshot;
  }

  /**
   * Increments and returns criterion_visit_number for a criterion entry.
   *
   * @param guidelineId - Stable guideline ID
   * @param criterionId - Stable criterion ID
   * @returns Visit number for this session entry
   */
  nextCriterionVisit(guidelineId: string, criterionId: string): number {
    const key = `${guidelineId}:${criterionId}`;
    const next = (this.criterionVisits.get(key) ?? 0) + 1;
    this.criterionVisits.set(key, next);
    return next;
  }

  /**
   * Returns the current visit number without incrementing.
   *
   * @param guidelineId - Stable guideline ID
   * @param criterionId - Stable criterion ID
   * @returns Current visit number (0 if never viewed)
   */
  getCriterionVisit(guidelineId: string, criterionId: string): number {
    return this.criterionVisits.get(`${guidelineId}:${criterionId}`) ?? 0;
  }

  /**
   * Returns true once per distinct identity for a surface; false on rerenders.
   *
   * @param surface - View surface category
   * @param identity - Stable key for the current entry
   * @returns Whether a view event should fire
   */
  claimViewIdentity(surface: ViewSurface, identity: string): boolean {
    if (this.lastViewIdentity[surface] === identity) return false;
    this.lastViewIdentity[surface] = identity;
    return true;
  }

  /** Allows Summary Viewed to fire again after leaving and returning. */
  clearSummaryView(): void {
    this.lastViewIdentity.summary = null;
  }

  /**
   * Stores pending Incorrect feedback set on Save Feedback click (before persist).
   * Consumed as feedback_saved when Next/Back successfully commits the draft.
   *
   * @param pending - Pending feedback action payload
   */
  setPendingFeedback(pending: PendingFeedbackAction): void {
    this.pendingFeedback = pending;
  }

  /**
   * Returns and clears pending feedback when it matches the criterion.
   *
   * @param criterionId - Criterion that just persisted
   * @returns Pending action, or null when none / mismatch
   */
  consumePendingFeedback(criterionId: string): PendingFeedbackAction | null {
    if (!this.pendingFeedback) return null;
    if (this.pendingFeedback.criterionId !== criterionId) return null;
    const pending = this.pendingFeedback;
    this.pendingFeedback = null;
    return pending;
  }

  /**
   * Drops pending feedback without emitting Feedback Saved (e.g. Correct selected).
   */
  clearPendingFeedback(): void {
    this.pendingFeedback = null;
  }
}
